import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { tituloService } from '@/services/tituloService';
import { FilterQuery, PaginationQuery, TituloStatus, Titulo } from '@/types';
import { formatCurrency, formatDate, isDateOverdue, cn } from '@/lib/utils';
import { TituloModal } from '@/components/Titulos/TituloModal';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye,
  Edit,
  AlertTriangle,
  Building2,
  User,
  Calendar
} from 'lucide-react';

export const TitulosPage: React.FC = () => {
  const [filters, setFilters] = useState<FilterQuery>({});
  const [pagination, setPagination] = useState<PaginationQuery>({
    page: 1,
    limit: 20,
    sortBy: 'vencimento',
    sortOrder: 'asc',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTitulo, setSelectedTitulo] = useState<Titulo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: titulosData,
    isLoading,

  } = useQuery({
    queryKey: ['titulos', filters, pagination],
    queryFn: () => tituloService.getAll(filters, pagination),
  });

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (status: TituloStatus | undefined) => {
    setFilters(prev => ({
      ...prev,
      status,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleCreateTitulo = () => {
    setSelectedTitulo(null);
    setIsModalOpen(true);
  };

  const handleEditTitulo = (titulo: Titulo) => {
    setSelectedTitulo(titulo);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTitulo(null);
  };

  const getStatusColor = (status: TituloStatus) => {
    switch (status) {
      case TituloStatus.ABERTO:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case TituloStatus.PARCIAL:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case TituloStatus.VENCIDO:
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case TituloStatus.FECHADO:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
  };

  return (
    <>
      <Helmet>
        <title>Títulos - Sistema de Cobrança</title>
      </Helmet>

      <div className="space-y-6" data-testid="page-titulos">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Títulos</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Gestão de títulos de cobrança
            </p>
          </div>

          <div className="mt-4 sm:mt-0">
            <Button onClick={handleCreateTitulo}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Título
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por número, empresa ou observações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Status filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={!filters.status ? "default" : "outline"}
                size="sm"
                onClick={() => handleStatusFilter(undefined)}
              >
                Todos
              </Button>
              {Object.values(TituloStatus).map((status) => (
                <Button
                  key={status}
                  variant={filters.status === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>

            {/* Clear filters */}
            <div className="flex justify-end">
              <Button
                onClick={handleClearFilters}
                variant="outline"
                size="sm"
                disabled={!searchTerm && Object.keys(filters).length === 0}
              >
                <Filter className="mr-2 h-4 w-4" />
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>
              Resultados
              {titulosData && 'pagination' in titulosData && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({(titulosData as any).pagination.total} títulos)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : titulosData && 'data' in titulosData && (titulosData as any).data.length > 0 ? (
              <div className="space-y-4">
                {(titulosData as any).data.map((titulo: any) => {
                  const isOverdue = isDateOverdue(titulo.vencimento) && titulo.status !== TituloStatus.FECHADO;
                  
                  return (
                    <div
                      key={titulo.id}
                      className={cn(
                        'p-4 border rounded-lg transition-colors hover:bg-accent/50',
                        isOverdue && 'border-red-300 bg-red-50/50 dark:border-red-700 dark:bg-red-950/20'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center space-x-4">
                            <h3 className="font-semibold">{titulo.numero}</h3>
                            <span
                              className={cn(
                                'px-2 py-1 rounded-full text-xs font-medium',
                                getStatusColor(titulo.status)
                              )}
                            >
                              {titulo.status}
                            </span>
                            {isOverdue && (
                              <div className="flex items-center text-red-600 dark:text-red-400">
                                <AlertTriangle className="h-4 w-4 mr-1" />
                                <span className="text-xs">Vencido</span>
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span>{titulo.company.name}</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span>Venc: {formatDate(titulo.vencimento)}</span>
                            </div>
                            
                            {titulo.operador && (
                              <div className="flex items-center space-x-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>{titulo.operador.name}</span>
                              </div>
                            )}
                            
                            <div className="font-semibold text-primary">
                              {formatCurrency(titulo.valor)}
                            </div>
                          </div>

                          {titulo.observacoes && (
                            <p className="text-sm text-muted-foreground">
                              {titulo.observacoes}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditTitulo(titulo)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {titulosData && 'pagination' in titulosData && (titulosData as any).pagination.pages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <div className="text-sm text-muted-foreground">
                                              Página {(titulosData as any).pagination.page} de {(titulosData as any).pagination.pages}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={(titulosData as any).pagination.page === 1}
                        onClick={() => handlePageChange((titulosData as any).pagination.page - 1)}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={(titulosData as any).pagination.page === (titulosData as any).pagination.pages}
                        onClick={() => handlePageChange((titulosData as any).pagination.page + 1)}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Nenhum título encontrado</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Título */}
      <TituloModal
        titulo={selectedTitulo}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};
